import {useEffect, useRef} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {useToastStore} from '../state/toast.js'

const SOUND_URL = '/audio/new-notification.mp3'

let audioSingleton = null

function playChime() {
    if (typeof window === 'undefined' || typeof Audio === 'undefined')
        return
    if (!audioSingleton) {
        try {
            audioSingleton = new Audio(SOUND_URL)
            audioSingleton.preload = 'auto'
            audioSingleton.volume = 0.6
        } catch {
            audioSingleton = null
            return
        }
    }
    try {
        audioSingleton.currentTime = 0
        const result = audioSingleton.play()
        if (result && typeof result.catch === 'function')
            result.catch(() => {})
    } catch {
        // browsers may block playback until the user interacts with the page
    }
}

/**
 * Watch the TanStack Query cache for new high-severity unread notifications
 * and surface a toast (with audible chime) when one appears while the app is
 * in the foreground.
 *
 * Only `alert` severity surfaces a toast; lower severities live in the inbox.
 *
 * Called once near the app root.
 */
export function useForegroundNotifications() {
    const queryClient = useQueryClient()
    const pushToast = useToastStore(state => state.pushToast)
    const seenRef = useRef(new Set())
    const bootedRef = useRef(false)

    useEffect(() => {
        const cache = queryClient.getQueryCache()
        const handle = (event) => {
            const key = event?.query?.queryKey
            if (!Array.isArray(key) || key[0] !== 'notifications')
                return
            const data = event.query.state.data
            const items = data?.notifications
            if (!Array.isArray(items))
                return

            if (!bootedRef.current) {
                bootedRef.current = true
                for (const n of items)
                    seenRef.current.add(n.id)
                return
            }

            for (const n of items) {
                if (seenRef.current.has(n.id))
                    continue
                seenRef.current.add(n.id)
                if (n.read || n.severity !== 'alert')
                    continue
                pushToast({
                    title: n.title,
                    body: n.body,
                    variant: 'alert'
                })
                playChime()
            }
        }
        const unsubscribe = cache.subscribe(handle)
        return () => unsubscribe()
    }, [queryClient, pushToast])
}
